import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services'
import { queryKeys, invalidateQueries } from '@/lib/query'
import type { Asset } from '@/types'

interface CreateAssetData {
  name: string
  description?: string
}

interface UpdateAssetData {
  name?: string
  description?: string
}

/**
 * Hook to fetch all assets for an organization.
 */
export function useAssets(orgId: string) {
  return useQuery({
    queryKey: queryKeys.assets.all(orgId),
    queryFn: () => api.get<Asset[]>('/assets'),
    enabled: !!orgId,
  })
}

/**
 * Hook to fetch a single asset by ID.
 */
export function useAsset(assetId: string) {
  return useQuery({
    queryKey: queryKeys.assets.detail(assetId),
    queryFn: () => api.get<Asset>(`/assets/${assetId}`),
    enabled: !!assetId,
  })
}

/**
 * Hook to create a new asset.
 */
export function useCreateAsset(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateAssetData) => api.post<Asset>('/assets', data),
    onSuccess: (newAsset) => {
      queryClient.setQueryData<Asset[]>(queryKeys.assets.all(orgId), (old) =>
        old ? [...old, newAsset] : [newAsset]
      )
      invalidateQueries.assets(orgId)
    },
  })
}

/**
 * Hook to update an asset.
 */
export function useUpdateAsset(assetId: string, orgId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateAssetData) =>
      api.put<Asset>(`/assets/${assetId}`, data),
    onSuccess: (updatedAsset) => {
      queryClient.setQueryData(queryKeys.assets.detail(assetId), updatedAsset)
      invalidateQueries.assets(orgId)
    },
  })
}

/**
 * Hook to delete an asset.
 */
export function useDeleteAsset(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (assetId: string) => api.delete<void>(`/assets/${assetId}`),
    onMutate: async (assetId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.assets.all(orgId) })
      const previous = queryClient.getQueryData<Asset[]>(queryKeys.assets.all(orgId))
      queryClient.setQueryData<Asset[]>(queryKeys.assets.all(orgId), (old) =>
        old?.filter((a) => a.id !== assetId)
      )
      return { previous }
    },
    onError: (_err, _assetId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.assets.all(orgId), context.previous)
      }
    },
    onSettled: () => {
      invalidateQueries.assets(orgId)
    },
  })
}
